import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Shield, Terminal, FlaskConical, Cpu, KeyRound, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export default function LoginPage() {
  const { login, mfaSetup, mfaVerify, resetMfa } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();

  const [step, setStep] = useState("credentials"); // credentials | mfa_setup | mfa_verify
  const [mode, setMode] = useState("simulated");
  const [email, setEmail] = useState("admin@dndi.org");
  const [password, setPassword] = useState("Admin@DNDi2026");
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [qrData, setQrData] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login({ email, password, mode });
      setChallengeToken(data.challenge_token);
      if (data.mfa_setup) {
        const s = await mfaSetup(data.challenge_token);
        setQrData(s.qr_data_url);
        setOtpauthUri(s.otpauth_uri);
        setStep("mfa_setup");
      } else {
        setStep("mfa_verify");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const triggerReset = async () => {
    if (!email || !password) {
      toast.error("Email + mot de passe requis pour réinitialiser la MFA");
      return;
    }
    if (!window.confirm(
      "Réinitialiser la MFA de ce compte ? L'ancien QR ne fonctionnera plus, vous devrez re-scanner le nouveau."
    )) return;
    setLoading(true);
    try {
      const data = await resetMfa({ email, password });
      setChallengeToken(data.challenge_token);
      const s = await mfaSetup(data.challenge_token);
      setQrData(s.qr_data_url);
      setOtpauthUri(s.otpauth_uri);
      setStep("mfa_setup");
      toast.success("MFA réinitialisée — scannez le nouveau QR code");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 6) return;
    setLoading(true);
    try {
      await mfaVerify({ challenge_token: challengeToken, code: otpCode });
      toast.success("Authentification réussie");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="min-h-screen w-full bg-[#050a0e] text-white flex" data-testid="login-page">
      {/* Left side */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden border-r border-cyan-900/30 cyber-grid-bg">
        <div className="absolute inset-0 hex-pattern opacity-20 pointer-events-none" />
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, rgba(0,229,255,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,255,136,0.1), transparent 40%)",
             }} />
             
        <div className="relative p-12 pt-16 z-10">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-16"
          >
            <div className="relative">
              <Shield className="h-8 w-8 text-cyan-400 cyber-glow-text" />
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute inset-0 rounded-full border border-cyan-400"
              />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400/70">
                DNDi · Zero Trust Platform
              </div>
              <div className="text-lg font-semibold tracking-tight cyber-glow-text text-white">
                Cloud Médical Sécurisé
              </div>
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.1] max-w-lg glitch-text"
          >
            L'accès aux données médicales,
            <span className="text-cyan-400 cyber-glow-text"> vérifié en continu</span>.
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-white/60 max-w-md"
          >
            Une plateforme conforme au <span className="font-mono text-cyan-300">NIST SP 800-207</span>, {" "}
            avec IdP intégré, moteur de politiques, ZTNA logique, micro-segmentation
            et SIEM interne — pensée pour DNDi et sa mission humanitaire.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 grid grid-cols-3 gap-4"
          >
            {[
              { icon: KeyRound, label: "MFA TOTP" },
              { icon: Cpu, label: "PDP + PEP" },
              { icon: FlaskConical, label: "4 scénarios" },
            ].map((f, i) => (
              <div key={f.label} className={`cyber-card neon-border rounded-sm p-3 bg-white/[0.02] stagger-${i+1}`}>
                <f.icon className="h-4 w-4 text-cyan-400" />
                <div className="mt-2 text-xs font-mono uppercase tracking-[0.2em] text-white/70">
                  {f.label}
                </div>
              </div>
            ))}
          </motion.div>


        </div>

        <div className="relative p-12 pb-10 text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400/40 z-10">
          Genève · Nairobi · Kinshasa · Rio · New Delhi · Kuala Lumpur
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-16 relative">
        <button
          data-testid="lang-toggle"
          onClick={() => setLang(lang === "fr" ? "en" : "fr")}
          className="absolute top-6 right-6 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400/50 hover:text-cyan-400 transition-colors border border-cyan-900/30 px-2 py-1.5 rounded-sm cyber-card"
        >
          {lang === "fr" ? "FR / EN" : "EN / FR"}
        </button>

        <div className="max-w-md w-full mx-auto cyber-glass p-8 rounded-lg border border-cyan-900/30 shadow-[0_0_30px_rgba(0,229,255,0.05)] relative">
          
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <Terminal className="h-5 w-5 text-cyan-400 cyber-glow-text" />
            <div className="text-sm font-semibold tracking-tight">DNDi Zero Trust</div>
          </div>

          <AnimatePresence mode="wait">
            {step === "credentials" && (
              <motion.div 
                key="credentials"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-3xl font-semibold tracking-tight glitch-text">{t("login")}</h2>
                <p className="mt-2 text-sm text-cyan-100/50 font-mono text-xs uppercase tracking-wider">
                  &gt; SÉLECTIONNEZ L'ENVIRONNEMENT PUIS AUTHENTIFIEZ-VOUS_
                </p>

                {/* Mode selector */}
                <div className="mt-8 grid grid-cols-2 gap-3 stagger-1">
                  <button
                    data-testid="mode-real-btn"
                    type="button"
                    onClick={() => setMode("real")}
                    className={cn(
                      "text-left p-4 rounded-sm transition-all cyber-card",
                      mode === "real"
                        ? "border border-cyan-500 shadow-[0_0_15px_rgba(0,229,255,0.3)] bg-cyan-950/30"
                        : "border border-white/10 hover:border-cyan-500/50"
                    )}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400 cyber-glow-text">
                      {t("mode_real")}
                    </div>
                    <div className="mt-1 text-xs text-white/60 leading-snug">
                      Environnement de production DNDi.
                    </div>
                  </button>
                  <button
                    data-testid="mode-simulated-btn"
                    type="button"
                    onClick={() => setMode("simulated")}
                    className={cn(
                      "text-left p-4 rounded-sm transition-all cyber-card",
                      mode === "simulated"
                        ? "border border-orange-500 shadow-[0_0_15px_rgba(255,153,0,0.3)] bg-orange-950/30"
                        : "border border-white/10 hover:border-orange-500/50"
                    )}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400 cyber-glow-text">
                      {t("mode_simulated")}
                    </div>
                    <div className="mt-1 text-xs text-white/60 leading-snug">
                      Bac-à-sable, scénarios de démo.
                    </div>
                  </button>
                </div>

                <form onSubmit={submitCredentials} className="mt-6 space-y-4">
                  <div className="space-y-2 stagger-2">
                    <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400/80">
                      {t("email")}
                    </Label>
                    <Input
                      id="email" type="email" required data-testid="login-email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="bg-black/50 border-cyan-900/50 focus-visible:ring-cyan-500 focus-visible:shadow-[0_0_10px_rgba(0,229,255,0.5)] font-mono text-cyan-50"
                    />
                  </div>
                  <div className="space-y-2 stagger-3">
                    <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400/80">
                      {t("password")}
                    </Label>
                    <Input
                      id="password" type="password" required data-testid="login-password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="bg-black/50 border-cyan-900/50 focus-visible:ring-cyan-500 focus-visible:shadow-[0_0_10px_rgba(0,229,255,0.5)] font-mono text-cyan-50"
                    />
                  </div>
                  <Button
                    data-testid="login-submit"
                    disabled={loading}
                    className="w-full bg-cyan-500/20 text-cyan-400 border border-cyan-500 hover:bg-cyan-500/40 hover:shadow-[0_0_15px_rgba(0,229,255,0.6)] font-mono tracking-widest relative overflow-hidden group stagger-4"
                  >
                    <div className="absolute inset-0 scan-line opacity-0 group-hover:opacity-100 transition-opacity" />
                    {loading ? (
                      <span className="flex items-center gap-2">
                        AUTHENTIFICATION EN COURS<span className="animate-pulse">_</span>
                      </span>
                    ) : t("submit")}
                  </Button>


                </form>


              </motion.div>
            )}

            {step === "mfa_setup" && (
              <motion.div
                key="mfa_setup"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-3xl font-semibold tracking-tight glitch-text text-cyan-400">{t("mfa_setup_title")}</h2>
                <p className="mt-2 text-sm text-cyan-100/50 font-mono text-xs uppercase tracking-wider">&gt; {t("mfa_setup_help")}</p>

                <div className="mt-8 flex flex-col items-center stagger-1">
                  {qrData ? (
                    <div className="relative scanner-corners p-2 inline-block bg-black/50 overflow-hidden">
                       <div className="absolute inset-0 scan-line opacity-50 pointer-events-none" />
                      <img
                        data-testid="mfa-qr"
                        src={qrData}
                        alt="MFA QR"
                        className="w-48 h-48 bg-white p-2 relative z-10"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-48 border border-cyan-500/30 rounded-sm animate-pulse bg-cyan-950/20 flex items-center justify-center">
                      <Activity className="h-8 w-8 text-cyan-500 animate-spin" />
                    </div>
                  )}
                  <div className="mt-6 max-w-full break-all font-mono text-[10px] text-cyan-400/40 text-center cyber-card p-2 border border-cyan-900/30 rounded">
                    {otpauthUri}
                  </div>
                </div>

                <form onSubmit={submitOtp} className="mt-8 space-y-4">
                  <Label className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400/80 flex items-center gap-2 stagger-2">
                    <Shield className="h-3 w-3 animate-pulse text-cyan-400" /> CODE TOTP_
                  </Label>
                  <div className="stagger-3">
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={setOtpCode}
                      data-testid="mfa-code-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="border-cyan-900/50 bg-black/50 text-cyan-400 font-mono text-lg focus-visible:shadow-[0_0_10px_rgba(0,229,255,0.5)] focus-visible:ring-cyan-500 focus-visible:ring-1" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    disabled={loading || otpCode.length < 6}
                    data-testid="mfa-verify-btn"
                    className="w-full bg-cyan-500/20 text-cyan-400 border border-cyan-500 hover:bg-cyan-500/40 hover:shadow-[0_0_15px_rgba(0,229,255,0.6)] font-mono tracking-widest relative overflow-hidden group stagger-4"
                  >
                     <div className="absolute inset-0 scan-line opacity-0 group-hover:opacity-100 transition-opacity" />
                    {loading ? "VERIFICATION EN COURS..." : t("verify")}
                  </Button>
                </form>
              </motion.div>
            )}

            {step === "mfa_verify" && (
              <motion.div
                key="mfa_verify"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-2">
                   <Shield className="h-8 w-8 text-cyan-400 animate-pulse cyber-glow-text" />
                   <h2 className="text-3xl font-semibold tracking-tight glitch-text">{t("mfa_title")}</h2>
                </div>
                <p className="mt-2 text-sm text-cyan-100/50 font-mono text-xs uppercase tracking-wider">&gt; {t("mfa_help")}_</p>

                <form onSubmit={submitOtp} className="mt-8 space-y-4">
                  <div className="stagger-1">
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={setOtpCode}
                      data-testid="mfa-code-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="border-cyan-900/50 bg-black/50 text-cyan-400 font-mono text-lg focus-visible:shadow-[0_0_10px_rgba(0,229,255,0.5)] focus-visible:ring-cyan-500 focus-visible:ring-1" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    disabled={loading || otpCode.length < 6}
                    data-testid="mfa-verify-btn"
                    className="w-full bg-cyan-500/20 text-cyan-400 border border-cyan-500 hover:bg-cyan-500/40 hover:shadow-[0_0_15px_rgba(0,229,255,0.6)] font-mono tracking-widest relative overflow-hidden group stagger-2"
                  >
                    <div className="absolute inset-0 scan-line opacity-0 group-hover:opacity-100 transition-opacity" />
                    {loading ? "VERIFICATION EN COURS..." : t("verify")}
                  </Button>
                </form>

                <button
                  type="button"
                  onClick={triggerReset}
                  data-testid="reset-mfa-link"
                  className="mt-6 w-full text-center text-xs font-mono text-orange-400/50 hover:text-orange-400 hover:cyber-glow-text transition-all stagger-3"
                >
                  &gt; Code invalide ou téléphone perdu ? Réinitialiser la MFA
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
